import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Upload } from 'lucide-react';

import useJobGradeApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ArchitectureCoverageNotice from '../../components/organization/ArchitectureCoverageNotice';

const Container = styled.div``;

// Kademeler (spec 2026-10-27 §7.1): seviye 1 = en alt; liste en kıdemliden başlar.
export default function JobGradesPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete, loadAll, reorder, refreshTrigger,
  } = useJobGradeApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow ? await update(selectedRow.id, formData) : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const openOrder = useCallback(async () => {
    const all = await loadAll();
    setOrderItems(all.map((g) => ({ id: g.id, label: g.code ? `${g.name} (${g.code})` : g.name })));
    setOrderOpen(true);
  }, [loadAll]);

  const handleOrderSave = useCallback(async (formData) => {
    const ids = formData.order && formData.order.length ? formData.order : orderItems.map((i) => i.id);
    const success = await reorder(ids);
    if (success) setOrderOpen(false);
    return success;
  }, [orderItems, reorder]);

  const orderSchema = useMemo(() => [
    { name: 'hint', type: 'notice', content: t('jobGrade.orderHint') },
    {
      name: 'order',
      type: 'sortableList',
      label: t('jobGrade.orderLabel'),
      items: orderItems,
      badge: (index, count) => count - index,
    },
  ], [t, orderItems]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: t('jobGrade.selectedCount', { count: ids.length }) });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'edit', label: t('jobGrade.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('jobGrade.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const toolbarActions = canWrite('EM') ? (
    <>
      <Button variant="outline" onClick={openOrder} data-action="reorder-grades">
        <ArrowUpDown size={14} />
        {' '}
        {t('jobGrade.reorder')}
      </Button>
      <Button variant="outline" onClick={() => history.push('/job-architecture/import')} data-action="import-architecture">
        <Upload size={14} />
        {' '}
        {t('jobArchitectureImport.open')}
      </Button>
    </>
  ) : null;

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('jobGrade.edit') : t('jobGrade.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <GenericFormDrawer
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        title={t('jobGrade.reorder')}
        schema={orderSchema}
        initialValues={{ order: orderItems.map((i) => i.id) }}
        onSave={handleOrderSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('jobGrade.deleteTitle')}
        message={t('jobGrade.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {canWrite('EM') && <ArchitectureCoverageNotice refreshKey={refreshTrigger} />}

      <DataTable
        title={t('jobGrade.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('EM')}
        onNewButtonClick={handleNewButtonClick}
        onRowsDelete={canWrite('EM') ? handleRowsDelete : undefined}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
        toolbarActions={toolbarActions}
      />
    </Container>
  );
}
